import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { buildArticleListSchema, type ArticleItem } from '@core-mkt/services/schema/article-schema.util';
import type { FeaturedArticle, FeaturedEntry } from './featured-article';

export interface FeaturedArticleSchemaData {
  /** Absolute URL of the current page — used to namespace the ItemList @id. */
  pageUrl: string;
  /** Site root for the Organization cross-reference and to build absolute article URLs. */
  siteUrl: string;
  /** Brand name used on the cross-referenced Organization. */
  organizationName: string;
  /** The Featured Article component data, including any cardCtaOverride entries. */
  data: FeaturedArticle | null;
}

const VARIANT_ID_FRAGMENT = 'articles-featured';
const DEFAULT_LIST_NAME = 'Featured Articles';

/**
 * Returns BlogPosting nodes + an ItemList for the featured-article component.
 * Sources article fields from each FeaturedEntry's Craft data (slug, title,
 * blogSummary, featuredEntryImage). Honors `cardCtaOverride` so an article's
 * url matches what the rendered card actually links to.
 *
 * Returns null when there are no entries to emit.
 */
export function getFeaturedArticleSchema(input: FeaturedArticleSchemaData | null): SchemaOrgNode[] | null {
  if (!input || !input.data) return null;

  const entries = input.data.featuredArticles ?? [];
  if (entries.length === 0) return null;

  const overrides = buildOverrideMap(input.data.cardCtaOverride);
  const items: ArticleItem[] = entries.map((entry, index) => buildArticleItem(entry, index, overrides, input.siteUrl));

  return buildArticleListSchema({
    listId: `${input.pageUrl}#${VARIANT_ID_FRAGMENT}`,
    listName: input.data.componentTitle?.trim() || DEFAULT_LIST_NAME,
    siteUrl: input.siteUrl,
    organizationName: input.organizationName,
    items,
  });
}

function buildArticleItem(
  entry: FeaturedEntry,
  index: number,
  overrides: Map<number, string>,
  siteUrl: string,
): ArticleItem {
  const url = overrides.get(index) ?? buildArticleUrl(entry.slug, siteUrl);
  const image = entry.featuredEntryImage?.[0]?.url;
  return {
    headline: entry.title ?? '',
    url,
    description: entry.blogSummary ?? undefined,
    image,
  };
}

/**
 * Mirrors `featured-article.component.ts` slugToPath conversion: Craft uses
 * double-dash separators (`drivers-ed--reviews`) which become path segments
 * (`drivers-ed/reviews`) on the live site.
 */
function buildArticleUrl(slug: string | undefined, siteUrl: string): string {
  if (!slug) return '';
  const root = siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`;
  const path = slug.replace(/--/g, '/');
  return `${root}${path}/`;
}

function buildOverrideMap(overrides: FeaturedArticle['cardCtaOverride'] | undefined): Map<number, string> {
  const map = new Map<number, string>();
  if (!overrides) return map;
  for (const override of overrides) {
    if (!override?.cardNumber || !override?.ctaCardOverrideLink) continue;
    const oneBased = Number.parseInt(override.cardNumber, 10);
    if (!Number.isFinite(oneBased) || oneBased < 1) continue;
    // SECURITY: only accept http(s) overrides. A CMS editor setting
    // `javascript:`/`data:`/`about:` would otherwise flow into the article's
    // `url` and `@id` — letting them collide with or shadow legitimate @ids
    // in the @graph dedup, plus surfacing as a canonical URL to SEO tools.
    const link = override.ctaCardOverrideLink;
    if (!isHttpUrl(link)) continue;
    map.set(oneBased - 1, link);
  }
  return map;
}

function isHttpUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}
